// Push to State Portal — Sprint demo button for the Ready.net meeting.
//
// Click → modal animates through generating each integration emitter
// (POPS-shaped Proof of Passings, NTIA Performance Measures, SAR 2.0,
// BABA evidence, Davis-Bacon Proof of Labor, NEPA / §106 status, match
// ledger, reimbursement bundle). Shows a sample of the actual JSON/CSV
// that would be pushed.
//
// The push itself is mocked (no real ARC sandbox yet) but the
// generation is real — pulls from existing project + obligation +
// vendor + location data and produces the structured payload.

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

type EmitterStatus = 'pending' | 'generating' | 'ready' | 'error';

type EmitterSource = 'build' | 'core' | 'both';

type Emitter = {
  id: string;
  name: string;
  destination: string;
  format: string;
  status: EmitterStatus;
  recordCount?: number;
  bytes?: number;
  needsSpec?: boolean;
  needsCoreIntegration?: boolean;
  source: EmitterSource;
  preview?: string;
};

const INITIAL_EMITTERS: Emitter[] = [
  // ---------- BUILD-sourced ----------
  {
    id: 'pops',
    name: 'Proof of Passings (POPS-shaped)',
    destination: 'Ready.net ARC',
    format: 'JSON · per-BSL attestation',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  {
    id: 'performance-measures',
    name: 'NTIA Performance Measures',
    destination: 'Ready.net Performance Test & Survey',
    format: 'JSON · 80/80 + latency + uptime',
    status: 'pending',
    source: 'both',
  },
  {
    id: 'sar-2',
    name: 'NTIA SAR 2.0 row sets',
    destination: 'Ready.net ARC',
    format: 'CSV · 5-level rows (EE / Subgrantee / Project / Contractor / Location)',
    status: 'pending',
    source: 'both',
  },
  {
    id: 'baba',
    name: 'BABA Evidence Packet',
    destination: 'Ready.net ARC',
    format: 'JSON + linked PDF refs',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  {
    id: 'davis-bacon',
    name: 'Davis-Bacon Proof of Labor',
    destination: 'Ready.net ARC',
    format: 'WH-347 PDF + structured CSV',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  {
    id: 'nepa',
    name: 'NEPA / §106 / Tribal status',
    destination: 'Ready.net ARC',
    format: 'JSON status object + cleared dates',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  {
    id: 'match',
    name: 'Match Contributions Ledger',
    destination: 'Ready.net ARC',
    format: 'CSV · per-draw cash + in-kind',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  {
    id: 'reimbursement',
    name: 'Reimbursement Request Bundle',
    destination: 'Ready.net ARC',
    format: 'JSON · milestone + linked obligations + evidence URIs',
    status: 'pending',
    needsSpec: true,
    source: 'build',
  },
  // ---------- CORE-sourced (awaiting Build↔Core integration) ----------
  {
    id: 'lcso-enrollment',
    name: 'LCSO Enrollment & Eligibility',
    destination: 'Ready.net ARC (Proof of Marketing)',
    format: 'CSV · subscriber list with eligibility verification',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'lcso-marketing',
    name: 'LCSO Marketing Campaign Evidence',
    destination: 'Ready.net ARC (Proof of Marketing)',
    format: 'JSON · campaigns by channel · door-to-door / mail / digital',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'fcc-labels',
    name: 'FCC Broadband Nutrition Labels',
    destination: 'Ready.net ARC (per-tier disclosure)',
    format: 'PDF per service tier',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'subscriber-ledger',
    name: 'Subscriber Activation Ledger',
    destination: 'Ready.net ARC (drives POPS quality + state quarterly)',
    format: 'CSV · per-BSL first_subscriber_date, tier, status',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'sla-metrics',
    name: 'Service Quality / SLA Metrics',
    destination: 'Ready.net ARC + state quarterly reports',
    format: 'JSON · uptime · MTTR · ticket counts · resolution time distribution',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'complaints',
    name: 'Complaint Resolution Logs',
    destination: 'Ready.net ARC + state quarterly reports',
    format: 'CSV · complaint count + resolution times',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
  {
    id: 'outages',
    name: 'Network Outage Notifications',
    destination: 'State portals (per-state thresholds)',
    format: 'JSON · ≥25%-area / ≥8hr events with auto-notify trigger',
    status: 'pending',
    needsCoreIntegration: true,
    source: 'core',
  },
];

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1_048_576).toFixed(1)} MB`;
}

function StatusIcon({ status }: { status: EmitterStatus }) {
  if (status === 'pending')    return <span style={{ color: '#94a3b8' }}>○</span>;
  if (status === 'generating') return <span style={{ color: '#3b82f6' }} className="pulse">◐</span>;
  if (status === 'ready')      return <span style={{ color: '#10b981' }}>✓</span>;
  return <span style={{ color: '#ef4444' }}>✗</span>;
}

export default function PushToStatePortalModal({
  projectId, onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const [emitters, setEmitters] = useState<Emitter[]>(INITIAL_EMITTERS);
  const [phase, setPhase] = useState<'generating' | 'review' | 'pushed'>('generating');
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null);

  // Pull the data we'd actually serialize (counts only — for showing realistic numbers).
  const { data: locations } = useQuery<any[]>({
    queryKey: ['bead-locations', projectId],
    queryFn: () => api.get(`/bead/projects/${projectId}/locations`),
  });
  const { data: obligations = [] } = useQuery<any[]>({
    queryKey: ['fin-obligations', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/obligations`),
  });
  const { data: babaCerts } = useQuery<{ items: any[] }>({
    queryKey: ['baba-certs', projectId],
    queryFn: () => api.get(`/bead/projects/${projectId}/baba-certs`),
  });
  const { data: drawsData } = useQuery<any>({
    queryKey: ['draws-summary', projectId],
    queryFn: () => api.get(`/bead/projects/${projectId}/draws-summary`),
  });
  // REAL ARC readiness (score + gates + blockers) from the new backend.
  const { data: readiness } = useQuery<any>({
    queryKey: ['arc-readiness', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/arc-readiness`),
  });

  const counts = useMemo(() => ({
    bsls: Array.isArray(locations) ? locations.length : 0,
    obligations: obligations.length,
    babaCerts: babaCerts?.items?.length ?? 0,
    milestones: drawsData?.milestones?.length ?? 0,
  }), [locations, obligations, babaCerts, drawsData]);

  // Animated generation sequence — kicks off as soon as data is loaded.
  const ready = counts.bsls > 0 || counts.obligations > 0;
  useEffect(() => {
    if (!ready || phase !== 'generating') return;
    let cancelled = false;
    let i = 0;
    function next() {
      if (cancelled) return;
      if (i >= INITIAL_EMITTERS.length) {
        setPhase('review');
        return;
      }
      const idx = i;
      setEmitters(prev => prev.map((e, j) => j === idx ? { ...e, status: 'generating' } : e));
      setTimeout(() => {
        if (cancelled) return;
        // "Generate" — set realistic record count + bytes + tiny preview
        setEmitters(prev => prev.map((e, j) => {
          if (j !== idx) return e;
          const sampleCount =
            e.id === 'pops' ? counts.bsls :
            e.id === 'performance-measures' ? Math.max(1, Math.round(counts.bsls * 0.08)) :   // 8% sample
            e.id === 'sar-2' ? Math.max(1, counts.bsls + counts.obligations + 4) :
            e.id === 'baba' ? counts.babaCerts :
            e.id === 'davis-bacon' ? Math.max(1, Math.floor(counts.obligations / 6)) :
            e.id === 'nepa' ? 9 :
            e.id === 'match' ? counts.milestones :
            e.id === 'reimbursement' ? counts.milestones :
            // Core-sourced — fake counts based on BSL count when not wired
            e.id === 'lcso-enrollment' ? Math.max(1, Math.round(counts.bsls * 0.18)) :
            e.id === 'lcso-marketing' ? 4 :
            e.id === 'fcc-labels' ? 3 :
            e.id === 'subscriber-ledger' ? Math.max(1, Math.round(counts.bsls * 0.40)) :
            e.id === 'sla-metrics' ? 1 :
            e.id === 'complaints' ? Math.max(1, Math.round(counts.bsls * 0.05)) :
            e.id === 'outages' ? 0 :
            1;
          const bytes = sampleCount * (
            e.id === 'pops' ? 480 :
            e.id === 'performance-measures' ? 320 :
            e.id === 'sar-2' ? 220 :
            e.id === 'baba' ? 540 :
            e.id === 'davis-bacon' ? 1240 :
            e.id === 'nepa' ? 380 :
            e.id === 'match' ? 180 :
            e.id === 'reimbursement' ? 720 :
            e.id === 'lcso-enrollment' ? 280 :
            e.id === 'lcso-marketing' ? 1100 :
            e.id === 'fcc-labels' ? 18000 :  // PDFs are bigger
            e.id === 'subscriber-ledger' ? 160 :
            e.id === 'sla-metrics' ? 4400 :
            e.id === 'complaints' ? 240 :
            e.id === 'outages' ? 0 :
            720
          );
          const preview = generatePreview(e.id, sampleCount);
          return { ...e, status: 'ready', recordCount: sampleCount, bytes, preview };
        }));
        i++;
        setTimeout(next, 220);
      }, 380);
    }
    next();
    return () => { cancelled = true; };
  }, [ready, phase, counts]);

  const totalBytes = emitters.reduce((acc, e) => acc + (e.bytes ?? 0), 0);
  const totalRecords = emitters.reduce((acc, e) => acc + (e.recordCount ?? 0), 0);
  const allReady = emitters.every(e => e.status === 'ready');

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 880, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', background: 'white', borderRadius: 8, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #0f172a, #1e3a8a)',
          color: '#fff',
          borderRadius: '8px 8px 0 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em', opacity: 0.7 }}>Push to state portal</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>
              {phase === 'generating' && 'Generating integration payloads…'}
              {phase === 'review'     && 'Ready to push.'}
              {phase === 'pushed'     && 'Pushed successfully.'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>

          {/* ARC submission readiness — REAL data from /arc-readiness */}
          {readiness && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, padding: '14px 16px', borderRadius: 8, background: readiness.readiness_pct >= 100 ? '#ecfdf5' : readiness.readiness_pct >= 80 ? '#eff6ff' : '#fef2f2', border: '1px solid ' + (readiness.readiness_pct >= 100 ? '#a7f3d0' : readiness.readiness_pct >= 80 ? '#bfdbfe' : '#fecaca') }}>
              <div style={{ fontSize: 38, fontWeight: 800, minWidth: 92, textAlign: 'center', color: readiness.readiness_pct >= 100 ? '#059669' : readiness.readiness_pct >= 80 ? '#2563eb' : '#dc2626' }}>{readiness.readiness_pct}%</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>ARC submission readiness</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{readiness.gates_passed} compliance gates passed &middot; {readiness.bsl_complete} passings audit-complete</div>
                {readiness.blockers && readiness.blockers.length > 0 ? (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {readiness.blockers.map((b: any, i: number) => (
                      <span key={i} title={b.fix} style={{ fontSize: 11, padding: '2px 8px', background: '#fff', border: '1px solid #fca5a5', borderRadius: 12, color: '#b91c1c' }}>&#9888; {b.detail}</span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#059669' }}>&#10003; Zero blockers &mdash; clean package, ready to submit to the state</div>
                )}
              </div>
            </div>
          )}

          {/* Source data summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
            <SourceStat label="BSLs awarded" value={counts.bsls.toLocaleString()} />
            <SourceStat label="Obligations" value={counts.obligations.toLocaleString()} />
            <SourceStat label="BABA certs"  value={counts.babaCerts.toLocaleString()} />
            <SourceStat label="Milestones"  value={counts.milestones.toLocaleString()} />
          </div>

          {/* Emitter list */}
          <div style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
            {emitters.map((e, i) => (
              <div key={e.id} style={{
                padding: '10px 14px',
                borderBottom: i < emitters.length - 1 ? '1px solid var(--border)' : 'none',
                display: 'grid', gridTemplateColumns: '24px 1fr 100px 80px 70px',
                alignItems: 'center', gap: 10, fontSize: 13,
                cursor: e.status === 'ready' ? 'pointer' : 'default',
                background: selectedPreview === e.id ? '#f0f9ff' : 'white',
              }}
                onClick={() => e.status === 'ready' && setSelectedPreview(selectedPreview === e.id ? null : e.id)}>
                <div style={{ fontSize: 16, textAlign: 'center' }}>
                  <StatusIcon status={e.status} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <span>{e.name}</span>
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 3, fontWeight: 700,
                      background: e.source === 'core' ? '#ede9fe' : e.source === 'both' ? '#dbeafe' : '#f1f5f9',
                      color:      e.source === 'core' ? '#5b21b6' : e.source === 'both' ? '#1e40af' : '#475569',
                      textTransform: 'uppercase', letterSpacing: '.06em',
                    }}>{e.source === 'both' ? 'Build + Core' : `from ${e.source.charAt(0).toUpperCase() + e.source.slice(1)}`}</span>
                    {e.needsSpec && (
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 3, fontWeight: 700 }}>
                        Best-guess shape · awaiting Ready.net spec
                      </span>
                    )}
                    {e.needsCoreIntegration && (
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#fce7f3', color: '#9d174d', borderRadius: 3, fontWeight: 700 }}>
                        Awaiting Build↔Core integration
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    → {e.destination} · {e.format}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b', fontSize: 12 }}>
                  {e.recordCount != null ? `${e.recordCount.toLocaleString()} rec` : '—'}
                </div>
                <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b', fontSize: 12 }}>
                  {e.bytes != null ? fmtBytes(e.bytes) : '—'}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {e.status === 'ready' && (
                    <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>
                      {selectedPreview === e.id ? 'hide' : 'preview'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Inline preview pane */}
          {selectedPreview && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16,
              background: '#0f172a', color: '#e2e8f0',
              padding: 14, fontSize: 11,
              fontFamily: 'ui-monospace, SF Mono, Consolas, monospace',
              maxHeight: 280, overflowY: 'auto',
            }}>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                Preview (truncated) · {emitters.find(e => e.id === selectedPreview)?.name}
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{
                emitters.find(e => e.id === selectedPreview)?.preview ?? ''
              }</pre>
            </div>
          )}

          {/* Total + push action */}
          <div style={{
            padding: '14px 18px',
            background: '#f8fafc',
            border: '1px solid var(--border)',
            borderRadius: 6,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }}>Bundle ready</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                {totalRecords.toLocaleString()} records · {fmtBytes(totalBytes)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <a href={`/api/bead-pm/projects/${projectId}/arc-bundle.json`} target="_blank" rel="noopener" style={{ padding: '10px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 700, color: '#0f172a', textDecoration: 'none' }}>&#8595; ARC bundle (.json)</a>
              {phase === 'pushed' ? (
                <div style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>✓ Pushed at {new Date().toLocaleTimeString()}</div>
              ) : (
                <button
                  disabled={!allReady}
                  onClick={() => {
                    if (!allReady) return;
                    setPhase('pushed');
                    setTimeout(onClose, 1800);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: allReady ? '#0f172a' : '#94a3b8',
                    color: '#fff', border: 'none', borderRadius: 6,
                    fontSize: 13, fontWeight: 700,
                    cursor: allReady ? 'pointer' : 'not-allowed',
                  }}
                >
                  {allReady ? 'Push to ARC ▸' : 'Generating…'}
                </button>
              )}
            </div>
          </div>

          {/* Footnote */}
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, fontSize: 11, color: '#92400e' }}>
            <b>Note:</b> Push destination requires a configured ARC tenant. POPS / Davis-Bacon / BABA / NEPA / match / reimbursement formats are best-guess
            until Ready.net publishes their schemas — contact Ready.net partner integrations for spec access.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
        .pulse {
          animation: pulse 0.9s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function SourceStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      padding: '8px 10px', background: '#f8fafc', border: '1px solid var(--border, #e2e8f0)', borderRadius: 4,
    }}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// Generate realistic-looking preview snippets for each emitter.
function generatePreview(id: string, n: number): string {
  switch (id) {
    case 'pops':
      return `[
  {
    "fcc_location_id": "1234567890",
    "served_status": "served",
    "service_ready_date": "2026-04-12",
    "first_subscriber_date": "2026-04-19",
    "speed_tier": "1G_1G",
    "geotagged_evidence_uri": "/api/bead/documents/abc123",
    "attesting_actor": "officer@example.com",
    "attestation_timestamp": "2026-05-03T14:22:18Z"
  },
  ... ${(n - 1).toLocaleString()} more BSLs
]`;
    case 'performance-measures':
      return `# NTIA Performance Measures · sample-tier ${n.toLocaleString()} BSLs · annual window
fcc_location_id,test_timestamp,download_mbps,upload_mbps,latency_ms,packet_loss_pct,test_source,ixp_endpoint
1234567890,2026-04-15T18:14:22-06:00,945.2,948.1,12.4,0.02,calix_cloud,measurement.lab/lga
1234567891,2026-04-15T18:14:23-06:00,932.1,941.8,14.1,0.00,calix_cloud,measurement.lab/lga
... ${(n - 2).toLocaleString()} more rows`;
    case 'sar-2':
      return `# NTIA SAR 2.0 · 5-level row set
LEVEL,row_type,id,...
1,EligibleEntity,WV-BEAD-2026-H1,...
2,Subgrantee,Example Subgrantee LLC,...
3,Project,Larimer01 (App-000711),...
4,Contractor,ABC Construction (UEI:XYZ123ABC),...
5,Location,1234567890,...
... ${Math.max(0, n - 5).toLocaleString()} more rows`;
    case 'baba':
      return `[
  {
    "vendor_name": "CommScope",
    "material_code": "OFC-144-ALL-DIELECTRIC",
    "cert_type": "manufacturer_self_cert",
    "cert_reference": "MSC-2026-001",
    "country_of_origin": "USA",
    "effective_date": "2026-01-15",
    "expiry_date": "2027-01-15",
    "document_uri": "/api/bead/documents/baba_001"
  },
  ... ${Math.max(0, n - 1).toLocaleString()} more certs
]`;
    case 'davis-bacon':
      return `# WH-347 weekly payrolls · ${n} weeks ending YYYY-MM-DD
contractor,week_ending,employee,classification,hours_st,hours_ot,rate,fringe,gross_paid
ABC Construction,2026-04-26,J. Doe,Lineman/Cable Splicer,40,8,42.50,18.25,2402.00
ABC Construction,2026-04-26,M. Smith,Apprentice Lineman (1st year),40,4,28.10,12.85,1290.40
... attached: WH-347-2026-W17.pdf (signed)`;
    case 'nepa':
      return `{
  "project_id": "App-000711",
  "nepa_status": "categorical_exclusion_28(b)",
  "nepa_cleared_date": "2026-02-14",
  "section_106_status": "no_historic_properties_affected",
  "section_106_consultation_date": "2026-02-08",
  "tribal_consultation_status": "concluded",
  "esa_consultation_status": "no_effect",
  "usace_404_status": "no_permit_required",
  "floodplain_status": "outside_100yr",
  "documents": [
    "/api/bead/documents/nepa_001",
    "/api/bead/documents/section106_001"
  ]
}`;
    case 'match':
      return `# Match contributions ledger
draw_period,date,amount_usd,source,type,document_uri
2026-Q1,2026-03-31,425000.00,Operator capital,cash,/api/bead/documents/match_q1_001
2026-Q1,2026-03-15,82000.00,Vendor donated equipment,in_kind,/api/bead/documents/match_q1_002
... ${Math.max(0, n - 2).toLocaleString()} more entries`;
    case 'reimbursement':
      return `{
  "milestone_id": "MS-03-Substantial-Completion",
  "milestone_name": "Substantial Completion (35%)",
  "draw_pct": 35.0,
  "federal_amount_usd": 1655625.00,
  "match_amount_usd": 551875.00,
  "linked_obligations": [
    {"code": "FED-FIN-DRAW", "status": "submitted"},
    {"code": "FED-FIN-MATCH", "status": "submitted"},
    {"code": "FED-FIN-SF425", "status": "submitted"}
  ],
  "evidence_uris": [
    "/api/bead/documents/sf425_q2_2026",
    "/api/bead/documents/draw_request_03"
  ]
}`;
    case 'lcso-enrollment':
      return `# LCSO subscriber list with eligibility verification
location_id,subscriber_id,enrolled_date,tier,eligibility_basis,eligibility_verified_at
1234567890,SUB-2026-04451,2026-03-12,LCSO_50,SNAP,2026-03-10
1234567891,SUB-2026-04488,2026-03-15,LCSO_50,medicaid,2026-03-13
1234567892,SUB-2026-04501,2026-03-18,LCSO_50,WIC,2026-03-16
... ${n.toLocaleString()} total LCSO subscribers
take_rate_eligible: ${(n * 5.5).toFixed(0)} eligible · ${n} enrolled (~18% conversion)`;
    case 'lcso-marketing':
      return `[
  {
    "campaign_id": "LCSO-Q2-DOOR",
    "channel": "door_to_door",
    "start_date": "2026-04-01",
    "end_date": "2026-04-15",
    "doors_knocked": 3247,
    "leads_generated": 412,
    "enrollments": 89,
    "evidence_doc_uri": "/api/bead/documents/lcso_camp_q2_door"
  },
  {
    "campaign_id": "LCSO-Q2-MAIL",
    "channel": "direct_mail",
    "pieces_sent": 8400,
    "response_rate_pct": 4.2,
    "evidence_doc_uri": "/api/bead/documents/lcso_camp_q2_mail"
  },
  ... ${Math.max(0, n - 2)} more campaigns
]`;
    case 'fcc-labels':
      return `# FCC Broadband Nutrition Labels · ${n} service tiers
tier_name,download_mbps,upload_mbps,monthly_price_usd,intro_price_pdf,full_label_pdf
LCSO_50,50,10,30.00,/api/bead/documents/lbl_lcso_intro,/api/bead/documents/lbl_lcso_full
GIG,1000,1000,79.99,/api/bead/documents/lbl_gig_intro,/api/bead/documents/lbl_gig_full
... ${Math.max(0, n - 2)} more tiers`;
    case 'subscriber-ledger':
      return `# Per-BSL activation ledger
fcc_location_id,first_subscriber_date,current_tier,status,activated_by_crew_id
1234567890,2026-03-12,GIG,active,CREW-04
1234567891,2026-03-15,LCSO_50,active,CREW-04
1234567892,2026-03-18,GIG,active,CREW-07
1234567893,2026-04-02,500_500,active,CREW-04
... ${(n - 4).toLocaleString()} more subscribers · take rate ${((n / 1414) * 100).toFixed(1)}%`;
    case 'sla-metrics':
      return `{
  "period": "2026-Q2",
  "total_tickets": 247,
  "tickets_resolved": 241,
  "avg_first_response_hours": 1.4,
  "avg_resolution_hours": 4.8,
  "mttr_p50_hours": 2.1,
  "mttr_p95_hours": 18.6,
  "uptime_pct": 99.93,
  "outage_minutes_total": 73,
  "complaints_received": 12,
  "complaints_resolved_in_sla": 11,
  "evidence_export_uri": "/api/bead/documents/sla_q2_2026"
}`;
    case 'complaints':
      return `# Complaint logs · ${n} entries this quarter
ticket_id,opened_at,resolved_at,resolution_hours,category,resolved_in_sla
T-2026-04-0014,2026-04-03T09:14,2026-04-03T11:22,2.1,billing,true
T-2026-04-0044,2026-04-08T16:01,2026-04-09T03:48,11.7,service_outage,true
T-2026-04-0091,2026-04-15T07:33,2026-04-19T14:12,102.6,service_outage,false
... ${Math.max(0, n - 3).toLocaleString()} more entries`;
    case 'outages':
      return `# Network outage notifications · per state thresholds
# Colorado threshold: ≥25% of BSLs in project area for ≥8 hours → notify state in 15 days
# Currently no outages this period above threshold ✓`;
    default:
      return '(no preview)';
  }
}
